/**
 * Miller welding machine modbus
 * @ref https://drive.google.com/file/d/1Crw9W3lW8ztJhKeRwn52TYPZISKVeBwu/view?usp=sharing
 */
import * as zod from 'zod';

import type { WeldMachineMode } from '../types/MillerWeldMachineState';

export const RETRY_INTERVAL_MS = 10000;

export const INITIALIZATION_TIMEOUT_MS = 5000;

// @see section 3.4.3 "In the case of using single register writes, there is a 200 ms delay after the last program parameter is received before the power source will respond to the request."
export const WELD_PROGRAM_LOAD_MS = 200;

// Wait up to this many MS for the machine to respond to a command. Usually we're looking for some modbus feedback.
export const WAIT_FOR_MACHINE_TO_RESPOND_MS = 4000;

// Poll interval for waiting for the machine to respond to a command.
export const WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS = 10;

// Coils: R+W booleans
export enum Coils {
  // Weld commands. Table 3-1
  WeldDisable = 0,
  ContactorOn = 1,
  GasEnable = 2,
  MotorForward = 3,
  MotorRetract = 4,
  TouchSenseEnable = 5,
}

// Discrete inputs: Read-only boolean
export enum DiscreteInputs {
  // Weld feedback - Table 3-2
  ArcDetected = 0,
  ContactorStatus = 1,
  GasStatus = 2,
  FeedForwardStatus = 3,
  FeedRetractStatus = 4,
  TouchSenseEnabled = 5,
  WireTouched = 6,
  WeldStateStatus = 7,
  SynergicProcess = 8,
  ErrorActive = 9,
  PSConnected = 10,
  Ready = 11,
}

// Input registers: Read-only numbers
export enum InputRegisters {
  // Weld feedback. Table 3-2
  ActualWFS = 0,
  ActualArcVoltage = 1,
  ActualArcCurrent = 2,
  ActualGasFlow = 3,
  RawHeat = 4,
  HeatInput = 5,
  // Weld setting feedback. Table 3-12
  PresetWFS = 6,
  PresetVoltage = 7,
  PresetArcLength = 8,
  PresetArcControl = 9,
  MemorySlot = 10,
  ActiveProcess = 11, // Table 3-4
  ActiveWiretype = 12, // Table 3-5 (Note: "Wiretype" rather than "WireType" is what is in docs.)
  ActiveWireSize = 13, // Table 3-6
  ActiveGasType = 14, // Table 3-7
  ErrorCode = 16,
  // Weld Sequence Settings (again). Table 3-12
  SequencePreflowEnable = 500,
  SequencePreflowTime = 501,
  SequenceRunInMode = 502,
  SequenceRunInWFS = 503,
  SequenceStartMode = 504,
  SequenceStartWFS = 505,
  SequenceStartVoltage = 506,
  SequenceStartArcLength = 507,
  SequenceStartDwellTime = 508,
  SequenceStartRampTime = 509,
  SequenceProfilePulseMode = 510,
  SequenceCraterMode = 511,
  SequenceBurnbackTime = 512,
  SequencePostflowEnable = 513,
  SequencePostflowTime = 514,
  SequenceRetractEnable = 515,
  SequenceRetractDistance = 516,
}

// Holding registers: R+W numbers
export enum HoldingRegisters {
  // Weld commands. Table 3-1
  PresetWFS = 0,
  PresetVoltage = 1,
  PresetArcLength = 2,
  PresetArcControl = 3,
  SelectMemorySlot = 4,
  // Weld Program Selection. Table 3-3
  WeldProcess = 1000, // Table 3-4. CV MIG/Accu-Pulse/HD MIG/RMD
  WeldWireType = 1001, // Table 3-5
  WeldWireSize = 1002, // Table 3-6
  WeldGasType = 1003, // Table 3-7
  // Weld Sequence Settings. Table 3-8
  SequencePreflowEnable = 500,
  SequencePreflowTime = 501,
  SequenceRunInMode = 502,
  SequenceRunInWFS = 503,
  SequenceStartMode = 504,
  SequenceStartWFS = 505,
  SequenceStartVoltage = 506,
  SequenceStartArcLength = 507,
  SequenceStartDwellTime = 508,
  SequenceStartRampTime = 509,
  SequenceProfilePulseMode = 510,
  SequenceCraterMode = 511,
  SequenceBurnbackTime = 512,
  SequencePostflowEnable = 513,
  SequencePostflowTime = 514,
  SequenceRetractEnable = 515,
  SequenceRetractDistance = 516,
  // Robot feedback. Table 3-13
  WeldTravelSpeed = 1500, // mm/sec. Setting 12 ~> 1.2mm/sec (notice typo)
}

// Weld Process Enum
export enum WeldProcess {
  CV_MIG = 0,
  Accu_Pulse = 1,
  HD_MIG = 2,
  RMD = 3,
  UNSET = 65535,
}

export const WeldMachineModeToEnum: Required<{
  [K in WeldMachineMode]: WeldProcess;
}> = {
  constantVoltage: WeldProcess.CV_MIG,
  pulse: WeldProcess.Accu_Pulse,
  hdMig: WeldProcess.HD_MIG,
  rmd: WeldProcess.RMD,
  unset: WeldProcess.UNSET,
};

export const WeldMachineModeFromEnum: Required<{
  [K in WeldProcess]: WeldMachineMode;
}> = {
  [WeldProcess.CV_MIG]: 'constantVoltage',
  [WeldProcess.Accu_Pulse]: 'pulse',
  [WeldProcess.HD_MIG]: 'hdMig',
  [WeldProcess.RMD]: 'rmd',
  [WeldProcess.UNSET]: 'unset',
};

export const WeldMachineModeLabels: Required<{
  [K in WeldMachineMode]: string;
}> = {
  constantVoltage: 'Constant voltage',
  pulse: 'Pulse',
  hdMig: 'HD MIG',
  rmd: 'RMD',
  unset: 'Unset',
};

// Wire Type Enum
export enum WireTypeRaw {
  Steel_ER70X = 0,
  Metal_Core_EXXC = 1,
  Flux_Core_EXXT = 2,
  Aluminum_4XXX = 3,
  Aluminum_4943 = 4,
  Aluminum_5XXX = 5,
  Stainless_Steel_ER3XX = 6,
  Stainless_Steel_ER4XX = 7,
  UNSET = 65535,
}

export const WireType = zod.nativeEnum(WireTypeRaw);
export type WireType = zod.infer<typeof WireType>;

// Wire Size Enum
export enum WireSizeRaw {
  Size_0_030 = 0,
  Size_0_035 = 1,
  Size_0_040 = 2,
  Size_0_045 = 3,
  Size_3_64 = 4,
  Size_0_052 = 5,
  Size_0_062 = 6,
  Size_0_078 = 7,
  Size_0_093 = 8,
  UNSET = 65535,
}

export const WireSizeLabels: Required<{
  [K in WireSizeRaw]: string;
}> = {
  [WireSizeRaw.Size_0_030]: '0.030 in',
  [WireSizeRaw.Size_0_035]: '0.035 in',
  [WireSizeRaw.Size_0_040]: '0.040 in',
  [WireSizeRaw.Size_0_045]: '0.045 in',
  [WireSizeRaw.Size_3_64]: '3/64 in',
  [WireSizeRaw.Size_0_052]: '0.052 in',
  [WireSizeRaw.Size_0_062]: '0.062 in (1/16)',
  [WireSizeRaw.Size_0_078]: '0.078 in (5/64)',
  [WireSizeRaw.Size_0_093]: '0.093 in (3/32)',
  [WireSizeRaw.UNSET]: 'Unset',
};

export const WireSize = zod.nativeEnum(WireSizeRaw);
export type WireSize = zod.infer<typeof WireSize>;

// Gas Type Enum
export enum GasTypeRaw {
  Argon_100 = 0,
  Argon_98_CO2_2 = 1,
  Argon_95_CO2_5 = 2,
  Argon_90_CO2_10 = 3,
  Argon_85_CO2_15 = 4,
  Argon_80_CO2_20 = 5,
  Argon_75_CO2_25 = 6,
  CO2_100 = 7,
  Argon_98_O2_2 = 8,
  Argon_95_O2_5 = 9,
  Helium_100 = 10,
  Helium_75_Argon_25 = 11,
  Helium_50_Argon_50 = 12,
  Helium_25_Argon_75 = 13,
  Argon_81_Helium_18_CO2_1 = 14,
  Argon_66_Helium_33_CO2_1 = 15,
  Helium_90_Argon_7_5_CO2_2_5 = 16,
  UNSET = 65535,
}

export const GasTypeLabels: Required<{
  [K in GasTypeRaw]: string;
}> = {
  [GasTypeRaw.Argon_100]: 'Argon 100%',
  [GasTypeRaw.Argon_98_CO2_2]: 'Argon 98% / CO₂ 2%',
  [GasTypeRaw.Argon_95_CO2_5]: 'Argon 95% / CO₂ 5%',
  [GasTypeRaw.Argon_90_CO2_10]: 'Argon 90% / CO₂ 10%',
  [GasTypeRaw.Argon_85_CO2_15]: 'Argon 85% / CO₂ 15%',
  [GasTypeRaw.Argon_80_CO2_20]: 'Argon 80% / CO₂ 20%',
  [GasTypeRaw.Argon_75_CO2_25]: 'Argon 75% / CO₂ 25%',
  [GasTypeRaw.CO2_100]: 'CO₂ 100%',
  [GasTypeRaw.Argon_98_O2_2]: 'Argon 98% / O₂ 2%',
  [GasTypeRaw.Argon_95_O2_5]: 'Argon 95% / O₂ 5%',
  [GasTypeRaw.Helium_100]: 'Helium 100%',
  [GasTypeRaw.Helium_75_Argon_25]: 'Helium 75% / Argon 25%',
  [GasTypeRaw.Helium_50_Argon_50]: 'Helium 50% / Argon 50%',
  [GasTypeRaw.Helium_25_Argon_75]: 'Helium 25% / Argon 75%',
  [GasTypeRaw.Argon_81_Helium_18_CO2_1]: 'Argon 81% / Helium 18% / CO₂ 1%',
  [GasTypeRaw.Argon_66_Helium_33_CO2_1]: 'Argon 66% / Helium 33% / CO₂ 1%',
  [GasTypeRaw.Helium_90_Argon_7_5_CO2_2_5]:
    'Helium 90% / Argon 7.5% / CO₂ 2.5%',
  [GasTypeRaw.UNSET]: 'Unset',
};

export const GasType = zod.nativeEnum(GasTypeRaw);
export type GasType = zod.infer<typeof GasType>;
